<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_FIXED_DEPOSIT_PRODUCT')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex flex-wrap align-items-center justify-content-lg-end">
            <b-col md="3">
              <SelectField
                id="status"
                v-model="productStatus"
                label=""
                :placeholder="$t('Select')"
                :options="translatedStatusOptions"
                :set-selected="fetchFDProducts"
                item="label"
                :reduce="option => option.value"
              />
            </b-col>
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="penaltyRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="productList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_FIXED_DEPOSIT_PRODUCT')"
              @click="onSelectFDProductItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectFDProductItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_FIXED_DEPOSIT_PRODUCT' : 'ACTIVATE_FIXED_DEPOSIT_PRODUCT')"
              @click="fDProductStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      no-close-on-backdrop
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="fdProductRules">
        <b-form @submit.prevent="handleSubmit">
          <b-row class="match-height">
            <!-- Product Name -->
            <b-col md="6">
              <InputField
                id="product-name"
                v-model="fdProductFormData.name"
                :label="$t('Name of product')"
                :placeholder="$t('Enter product name')"
                type="text"
                :error-name="$t('Product Name')"
                rules="required"
                :readonly="isReadonly"
                onpaste="return false;"
                :is-allow-special-characters="false"
              />
            </b-col>
            <!-- Product Name -->

            <!-- Product Code -->
            <b-col md="6">
              <InputField
                id="product-code"
                v-model.number="fdProductFormData.code"
                :label="$t('Product Code')"
                :placeholder="$t('Enter product code')"
                type="number"
                :error-name="$t('Product Code')"
                rules="required|integer|length:3"
                :readonly="isEditProductCode"
              />
            </b-col>
            <!-- Product Code -->
          </b-row>

          <b-row class="match-height">
            <!-- Default Applicable Interest -->
            <b-col md="6">
              <SelectField
                id="default-application-interest"
                v-model="fdProductFormData.interestID"
                :label="$t('Default Applicable Interest')"
                :reduce="option => option.value"
                :options="creditInterestOptions"
                :placeholder="$t('Select')"
                :error-name="$t('Default Applicable Interest')"
                rules="required"
                item="label"
                :set-selected="setProductCreditInterestFee"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Default Applicable Interest -->

            <!--  Default Interest Rate -->
            <b-col>
              <InputField
                id="default-interest-rate"
                v-model.number="fdProductFormData.interestRate"
                :label="$t('Default Interest Rate (%)')"
                type="number"
                for="default-interest-rate"
                readonly
              />
            </b-col>
            <!--  Default Interest Rate -->
          </b-row>

          <b-row class="match-height">
            <!-- Interest Expense Account -->
            <b-col md="6">
              <SelectField
                id="interest-expense-amount"
                v-model="fdProductFormData.interestExpenseAccountNumber"
                :label="$t('Interest Expense Account')"
                :placeholder="$t('Select')"
                :options="interestExpanseAccountOptions"
                item="label"
                :reduce="option => option.value"
                :error-name="$t('Interest Expense Account')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Interest Expense Account -->

            <!-- Interest Payable Account -->
            <b-col md="6">
              <SelectField
                id="interest-payable-account"
                v-model="fdProductFormData.interestPayableAccountNumber"
                :label="$t('Interest Payable Account')"
                :placeholder="$t('Select')"
                :options="interestPayableOptions"
                item="label"
                :reduce="option => option.value"
                :error-name="$t('Interest Payable Account')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Interest Payable Account -->

            <!-- Prepaid Interest Expense Account -->
            <b-col md="6">
              <SelectField
                id="interest-payable-account"
                v-model="fdProductFormData.prepaidExpenseAccountNumber"
                :label="$t('Prepaid Interest Expense Account')"
                :placeholder="$t('Select')"
                :options="prepaidInterestExpanseAccountOptions"
                item="label"
                :reduce="option => option.value"
                :error-name="$t('Prepaid Interest Expense Account')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Prepaid Interest Expense Account -->
          </b-row>

          <b-row class="match-height">
            <!-- Withholding Tax Account -->
            <b-col md="6">
              <SelectField
                id="withholding-tax-account"
                v-model="fdProductFormData.whTaccountNumber"
                :label="$t('Withholding Tax Account')"
                :placeholder="$t('Select')"
                :options="withholdingTaxAccountOptions"
                item="label"
                :reduce="option => option.value"
                :error-name="$t('Withholding Tax Account')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Withholding Tax Account -->

            <!-- Default Tenure (In Days) -->
            <b-col md="6">
              <InputField
                id="product-code"
                v-model.number="fdProductFormData.defaultTenure"
                :label="$t('Default Tenure (In Days)')"
                :placeholder="$t('Enter default tenure')"
                type="number"
                :error-name="$t('Default Tenure')"
                rules="required|integer"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Default Tenure (In Days) -->

            <!-- Pre-liquidation Charge (%) -->
            <b-col md="6">
              <InputField
                id="pre-liquidation-charge"
                v-model.number="fdProductFormData.partLiquidationCharge"
                :label="$t('Pre-liquidation Charge (%)')"
                :placeholder="$t('Enter Pre-liquidation Charge')"
                type="number"
                :error-name="$t('Pre-liquidation Charge')"
                rules="required|max-value:100|positive"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Pre-liquidation Charge (%) -->
            <!--  Currency -->
            <b-col md="6">
              <SelectField
                id="currency-code"
                v-model="fdProductFormData.currencyCode"
                :reduce="option => option"
                :label="$t('Currency')"
                :options="currencyCodeOptions"
                :placeholder="$t('Select')"
                :error-name="actionType === 'Save' ? $t('Currency') : ''"
                :rules="actionType === 'Save' ? 'required' : ''"
                item="label"
                :readonly="isEditProductCode"
              />
            </b-col>
            <!--  Currency -->
          </b-row>
          <ValidationObserver /></b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchFDProducts('', 'main-filter')"
      @hidden="resetFilters"
    >
      <b-row class="match-height">
        <!-- Product Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.name"
            :label="$t('Name of Product')"
            :placeholder="$t('Enter product name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Product Name -->

        <!-- Product Code -->
        <b-col md="6">
          <InputField
            id="code"
            v-model="filter.code"
            :label="$t('Product Code')"
            :placeholder="$t('Enter product code')"
            type="number"
            rules="integer|length:3"
          />
        </b-col>
        <!-- Product Code -->

        <!--Currency  -->
        <b-col md="6">
          <SelectField
            id="currency-code"
            v-model="filter.currencyCode"
            :label="$t('Currency')"
            :reduce="option => option"
            :options="currencyCodeOptions"
            item="label"
            :placeholder="$t('Select currency')"
          />
        </b-col>
        <!--Currency  -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="translatedStatusOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BBadge,
  BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required, amount, positive } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { toastAlert } from '@core/mixins/toast'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toAmount } from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import { toDateWithYear } from '../../../../helpers/formatDate'
import getStatusColor from '../../../../helpers/getStatusColor'
import Button from '../../../components/button/Button.vue'
import { accountPermissions } from '../../../../helpers/permissions'

const statusOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'InActive', value: 'InActive' },
]
export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    BTable,
    BSpinner,
    BModal,
    BForm,
    ValidationObserver,
    BDropdown,
    BDropdownItem,
    InputField,
    SelectField,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
  },
  mixins: [modalMixins, toastAlert, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      sortBy: 'penaltyValue',
      showSpinner: false,
      isEditProductCode: false,
      actionType: 'Save',
      required,
      amount,
      positive,
      getStatusColor,
      interestConditionBasis: '',
      modalTitle: this.$t('Create New Fixed Deposit'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Name of product' },
        { key: 'code', sortable: true, label: 'Product Code' },
        { key: 'currencyCode', sortable: true, label: 'Currency' },
        { key: 'defaultTenure', sortable: true, label: 'Tenure (Days)' },
        { key: 'interestRate', sortable: true, label: 'Default Rate (%)' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      fdProductFormData: {
        name: '',
        code: '',
        interestID: '',
        interestRate: '',
        defaultTenure: '',
        interestExpenseAccountNumber: '',
        interestPayableAccountNumber: '',
        prepaidExpenseAccountNumber: '',
        whTaccountNumber: '',
        partLiquidationCharge: '',
        currencyCode: '',
      },

      filter: {
        name: '',
        code: '',
        currencyCode: '',
        status: 'Active',
        type: 'FixedDeposit',
        sortColumn: '',
      },
      interestExpanseAccountOptions: [],
      prepaidInterestExpanseAccountOptions: [],
      interestPayableOptions: [],
      withholdingTaxAccountOptions: [],
      productList: [],
      creditInterestOptions: [],
      toAmount,
      productStatus: 'Active',
      permission: '',
      currencyCodeOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      incomeGlOptions: 'account/incomeGlOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedStatusOptions() {
      return statusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      // eslint-disable-next-line no-undef
      this.fetchFDProducts('', 'main-filter')
    },
    perPage(newValue) {
      this.perPage = newValue
      // eslint-disable-next-line no-undef
      this.fetchFDProducts('', 'main-filter')
    },
  },
  mounted() {
    this.getPermission()
    this.fetchFDProducts(this.productStatus)
    this.getAllCreditInterest()
    this.interestExpanseAccount()
    this.prepaidExpenseAccount()
    this.interestPayableAccount()
    this.withholdingTaxAccount()
    this.fetchAllCurrencies()
  },
  methods: {
    ...mapActions({
      getFDProducts: 'account/getProducts',
      getCreditInterest: 'account/getInterestList',
      getInterestById: 'account/getInterestById',
      getGLAccountsByAccountType: 'account/getGLAccountsByList',
      createFDProduct: 'account/createFDProductAction',
      updateFDProduct: 'account/updateFDProductAction',
      updateFDProductStatus: 'account/updateStatus',
      getAllCurrencies: 'account/getAllCurrenciesAction',
      getGeneralLedger: 'account/getGeneralLedgerList',
    }),
    getPermission() {
      if (this.actionType === 'save' || this.actionType === 'add') {
        this.permission = 'CREATE_FIXED_DEPOSIT_PRODUCT'
        return
      }
      if (this.actionType === 'edit') {
        this.permission = 'MODIFY_FIXED_DEPOSIT_PRODUCT'
        return
      }
      this.permission = 'VIEW_FIXED_DEPOSIT_PRODUCT'
    },
    async fetchFDProducts(productStatus, type) {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getFDProducts({
          search: type === 'main-filter' ? this.filter : { status: productStatus, type: 'FixedDeposit' },
          page: this.currentPage,
          limit: this.perPage,
          permission: accountPermissions.VIEW_FIXED_DEPOSIT_PRODUCT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productList = data.data
          this.totalCount = data.recordCount
        } else {
          this.productList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.fdProductRules.validate().then(async success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const fdText = this.$t('this Fixed Deposit Product')
          this.confirm(this.FDProductAction, { title: '', text: `${areYouSure} ${this.buttonLabel} ${fdText}?` })
        }
      })
    },
    async FDProductAction() {
      const action = `${this.buttonLabel === 'Save' ? this.$t('Save') : this.$t('Update')}`
      try {
        this.isLoading = true
        const formData = {
          ...this.fdProductFormData,
          interestExpenseAccountNumber: this.fdProductFormData.interestExpenseAccountNumber.toString(),
          interestPayableAccountNumber: this.fdProductFormData.interestPayableAccountNumber.toString(),
          prepaidExpenseAccountNumber: this.fdProductFormData.prepaidExpenseAccountNumber.toString(),
          whTaccountNumber: this.fdProductFormData.whTaccountNumber.toString(),
        }
        console.log('DATA', formData)
        const response = this.buttonLabel === 'Save'
          ? await this.createFDProduct(formData)
          : await this.updateFDProduct(formData)

        const { status } = await response
        if (status === 200) {
          // eslint-disable-next-line no-undef
          this.fetchFDProducts('', 'main-filter')
          this.hideModal('modal-lg')
          const requestToText = this.$t('Your request to')
          const text = this.$t('Fixed Deposit Product was successful')
          this.alertSuccess({ text: `${requestToText} ${action} ${text}` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const requestToText = this.$t('Your request to')
          const reasonText = this.$t('Fixed Deposit Product failed. Reason')
          this.alertError({ text: `${requestToText} ${action} ${reasonText}: ${responseError.errorMessage}` })
        })
        this.isLoading = false
      } finally {
        this.isLoading = false
      }
    },
    async fetchAllCurrencies() {
      try {
        const response = await this.getAllCurrencies({ permission: accountPermissions.VIEW_SAVING_CURRENT_PRODUCT })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.currencyCodeOptions = data.data.filter(item => item.status === 'Active').reduce((acc, curr) => [...acc, curr.code], [])
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    onSelectFDProductItem(items, activity, val) {
      try {
        this.fdProductFormData = { ...items }
        this.isEditProductCode = true
        this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
        this.modalTitle = val === 'edit' ? this.$t('Edit Fixed Deposit Product') : this.$t('Fixed Deposit Product')
        this.buttonLabel = this.$t('Update')
        this.$root.$emit('bv::show::modal', 'modal-lg')
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fDProductStatus(items) {
      const { id, name } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? accountPermissions.DEACTIVATE_FIXED_DEPOSIT_PRODUCT : accountPermissions.ACTIVATE_FIXED_DEPOSIT_PRODUCT

      try {
        const updateStatus = async () => {
          this.showSpinner = true
          const statusValues = {
            id,
            name,
          }
          const response = await this.updateFDProductStatus({
            statusValues, actionType, routeName: 'Product', permission,
          })
          if (response.status === 200) {
            // eslint-disable-next-line no-undef
            this.fetchFDProducts('', 'main-filter')
            const requestToText = this.$t('Your request to')
            const text = this.$t('Fixed Deposit Product was successful')
            this.alertSuccess({ text: `${requestToText} ${action} ${text}` })
            this.showSpinner = false
          }
        }
        const areYouSure = this.$t('Are you sure you want to')
        const fdText = this.$t('this Fixed Deposit Product')
        this.confirm(updateStatus, { title: '', text: `${areYouSure} ${action} ${fdText}?` })
      } catch (error) {
        const requestToText = this.$t('Your request to')
        const reasonText = this.$t('Fixed Deposit Product failed. Reason:')
        this.alertError({ text: `${requestToText} ${action} ${reasonText}: ${error}` })
      } finally {
        this.showSpinner = false
      }
    },
    // Get Applicable Interest
    async getAllCreditInterest() {
      try {
        const response = await this.getCreditInterest({
          search: '',
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_FIXED_DEPOSIT_PRODUCT,
        })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.creditInterestOptions = data.data.filter(dataItem => dataItem.status === 'Active' && dataItem.type === 'Credit').map(item => ({ label: item.name, value: item.id }))
        }
      } catch (error) {
        this.toastAlert({ title: error })
      }
    },
    async setProductCreditInterestFee(selectedValue) {
      try {
        const response = await this.getInterestById({ id: selectedValue, permission: accountPermissions.VIEW_FIXED_DEPOSIT_PRODUCT })
        const { status, data } = response
        if (status === 200) {
          this.fdProductFormData.interestRate = data.data.rate
        }
      } catch (error) {
        this.alertError({ error })
      }
    },
    async interestExpanseAccount() {
      try {
        const interestExpanseAccountResponse = await this.getGLAccountsByAccountType({ id: 14, permission: accountPermissions.VIEW_FIXED_DEPOSIT_PRODUCT })
        const { data, status } = await interestExpanseAccountResponse
        if (status === 200 && data.data) {
          this.interestExpanseAccountOptions = data.data
            .filter(item => item.status === 'Active')
            .map(item => ({
              label: `${item.name} - ${item.code}`,
              value: item.code.toString(),
            }))
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    async prepaidExpenseAccount() {
      try {
        const response = await this.getGeneralLedger({
          search: { level2Code: 10600000 },
          permission: this.permission,
        })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.prepaidInterestExpanseAccountOptions = data.data
            .filter(item => item.status === 'Active')
            .map(item => ({
              label: `${item.name} - ${item.code}`,
              value: item.code.toString(),
            }))
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    async interestPayableAccount() {
      try {
        const interestPayableAccountResponse = await this.getGLAccountsByAccountType({ id: 12, permission: accountPermissions.VIEW_FIXED_DEPOSIT_PRODUCT })
        const { data, status } = await interestPayableAccountResponse
        if (status === 200 && data.data) {
          this.interestPayableOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: `${item.name} - ${item.code}`, value: item.code.toString() }))
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    async withholdingTaxAccount() {
      try {
        const withholdingTaxAccountResponse = await this.getGLAccountsByAccountType({ id: 27, permission: accountPermissions.VIEW_FIXED_DEPOSIT_PRODUCT })
        const { data, status } = await withholdingTaxAccountResponse
        if (status === 200 && data.data) {
          this.withholdingTaxAccountOptions = data.data.filter(item => item.status === 'Active').map(item => ({ label: `${item.name} - ${item.code}`, value: item.code.toString() }))
        }
      } catch (error) {
        this.handleError(error)
      }
    },

    resetModal() {
      this.fdProductFormData = { ...null }
      this.isReadonly = false
      this.isLoading = false
      this.isEditProductCode = false
      this.modalTitle = this.$t('Create New Fixed Deposit')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = {
        name: '',
        code: '',
        currencyCode: '',
        status: 'Active',
        type: 'FixedDeposit',
      }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchFDProducts('Active')
    },
  },
}
</script>
